import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Plus, Edit2, Trash2, X, Save, Mail, RefreshCw, Loader2 } from 'lucide-react';

const ICON_OPTIONS = ['shield','file-text','headphones','book-open','pen-tool','users','bookmark','message-circle','sparkles'];

export default function ResourcesEditor() {
  const [items, setItems] = useState([]);
  const [leads, setLeads] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showLeads, setShowLeads] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  const [reseedMsg, setReseedMsg] = useState('');

  const load = async () => {
    const [r, l] = await Promise.all([
      api.get('/resources').then(r => r.data).catch(() => []),
      api.get('/admin/leads').then(r => r.data).catch(() => []),
    ]);
    setItems(r); setLeads(l);
  };
  useEffect(() => { load(); }, []);

  const del = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    await api.delete(`/admin/resources/${r.id}`);
    load();
  };

  return (
    <div data-testid="admin-resources">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">Resources & Leads</div>
          <div className="font-display uppercase text-2xl mt-1">{items.length} resources · {leads.length} email leads</div>
        </div>
        <div className="flex gap-2">
          <button onClick={async () => {
            if (!window.confirm('Push the latest set of resource cards (Bookshelf, Journal Guide, Vision Board, Community, Mick Coach). Existing cards with the same ID get updated. Your custom lead-magnet PDF link is preserved. Continue?')) return;
            setReseeding(true); setReseedMsg('');
            try {
              const r = await api.post('/admin/resources/reseed');
              setReseedMsg(`Reset complete. ${r.data.total} resources now live.`);
              load();
              setTimeout(() => setReseedMsg(''), 5000);
            } catch (e) {
              setReseedMsg(e?.response?.data?.detail || 'Reset failed.');
            } finally { setReseeding(false); }
          }} disabled={reseeding} className="h-11 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-sm font-semibold border border-[color:var(--kc-gold)] text-ink hover:bg-gold-soft transition-colors disabled:opacity-50" data-testid="admin-reseed-resources">
            {reseeding ? <><Loader2 size={14} className="animate-spin"/> Resetting…</> : <><RefreshCw size={14}/> Reset to latest defaults</>}
          </button>
          <button onClick={() => setShowLeads(!showLeads)} className="h-11 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-sm font-semibold border border-kc-strong hover:bg-surface-2 transition-colors" data-testid="admin-show-leads"><Mail size={14}/> {showLeads ? 'Hide' : 'View'} leads ({leads.length})</button>
          <button onClick={() => setEditing({ id: '', category: '', title: '', description: '', cta_label: '', cta_href: '', icon: 'file-text', order: items.length, highlight: false, is_lead_magnet: false })} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] transition-colors" data-testid="admin-new-resource"><Plus size={16}/> New Resource</button>
        </div>
      </div>
      {reseedMsg && <div className="mb-4 p-3 bg-gold-soft border border-[color:var(--kc-gold)] text-ink rounded-[var(--kc-radius-sm)] text-sm font-semibold" data-testid="reseed-message">{reseedMsg}</div>}

      {showLeads && (
        <div className="mb-6 bg-white border border-kc rounded-[var(--kc-radius-md)] overflow-hidden" data-testid="admin-leads-list">
          <div className="p-4 border-b border-kc font-semibold flex items-center gap-2"><Mail size={16}/> Email Leads ({leads.length})</div>
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-left text-xs uppercase tracking-wider"><tr><th className="p-3">Email</th><th className="p-3">Source</th><th className="p-3">Date</th></tr></thead>
            <tbody>
              {leads.map((l, i) => (
                <tr key={i} className="border-t border-kc"><td className="p-3 font-semibold">{l.email}</td><td className="p-3 text-muted-kc text-xs">{l.source}</td><td className="p-3 text-muted-kc text-xs">{new Date(l.created_at).toLocaleString()}</td></tr>
              ))}
              {leads.length === 0 && <tr><td colSpan={3} className="p-6 text-center text-muted-kc text-sm">No leads yet. Add the lead magnet to your Resources page.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      <div className="space-y-3">
        {items.map((r) => (
          <div key={r.id} className="bg-white border border-kc rounded-[var(--kc-radius-md)] p-4 flex items-start gap-4" data-testid={`admin-resource-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.18em] uppercase text-muted-kc">{r.category}{r.highlight && <span className="ml-2 bg-[color:var(--kc-gold)] text-black px-2 py-0.5 rounded-full text-[9px] font-bold">HIGHLIGHT</span>}{r.is_lead_magnet && <span className="ml-2 bg-ink text-white px-2 py-0.5 rounded-full text-[9px] font-bold">LEAD MAGNET</span>}</div>
              <div className="font-display uppercase text-xl mt-1">{r.title}</div>
              <div className="text-sm text-muted-kc mt-1 line-clamp-2">{r.description}</div>
              <div className="text-xs text-muted-kc mt-2">→ {r.cta_label} ({r.cta_href || 'no link'}) · Icon: {r.icon} · Order: {r.order}</div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => setEditing(r)} className="p-2 hover:bg-surface-2 rounded" data-testid={`admin-edit-resource-${r.id}`}><Edit2 size={16}/></button>
              <button onClick={() => del(r)} className="p-2 hover:bg-red-50 text-[color:var(--kc-danger)] rounded" data-testid={`admin-del-resource-${r.id}`}><Trash2 size={16}/></button>
            </div>
          </div>
        ))}
      </div>

      {editing && <ResourceEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ResourceEditor({ initial, onClose, onSaved }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({ ...initial });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    setSaving(true); setErr('');
    try {
      if (isNew) await api.post('/admin/resources', form);
      else await api.put(`/admin/resources/${initial.id}`, form);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="resource-editor-modal">
      <div className="bg-white rounded-[var(--kc-radius-md)] w-full max-w-xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-kc"><div className="font-display uppercase text-2xl">{isNew ? 'New Resource' : 'Edit Resource'}</div><button onClick={onClose} className="p-1 text-muted-kc" data-testid="re-close"><X size={20}/></button></div>
        <div className="p-5 space-y-3">
          <Field label="Category" value={form.category} onChange={(v) => set('category', v)} testid="re-category"/>
          <Field label="Title" value={form.title} onChange={(v) => set('title', v)} testid="re-title"/>
          <Field label="Description" multiline value={form.description} onChange={(v) => set('description', v)} testid="re-desc"/>
          <div className="grid grid-cols-2 gap-3">
            <Field label="CTA label" value={form.cta_label} onChange={(v) => set('cta_label', v)} testid="re-cta-label"/>
            <Field label="CTA link / href" value={form.cta_href} onChange={(v) => set('cta_href', v)} testid="re-cta-href"/>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Icon</label>
            <select value={form.icon} onChange={(e) => set('icon', e.target.value)} className="kc-rinput" data-testid="re-icon">
              {ICON_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>
          <Field label="Order (lower = first)" value={form.order} onChange={(v) => set('order', parseInt(v) || 0)} testid="re-order"/>
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.highlight} onChange={(e) => set('highlight', e.target.checked)} data-testid="re-highlight"/> Highlight card (top)</label>
            <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_lead_magnet} onChange={(e) => set('is_lead_magnet', e.target.checked)} data-testid="re-leadmagnet"/> Lead magnet (email capture)</label>
          </div>
          {err && <div className="text-sm text-[color:var(--kc-danger)]">{err}</div>}
        </div>
        <div className="p-5 border-t border-kc flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-4 rounded-[var(--kc-radius-sm)] hover:bg-surface-2" data-testid="re-cancel">Cancel</button>
          <button onClick={save} disabled={saving || !form.title} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black font-semibold hover:bg-[color:var(--kc-gold-2)] disabled:opacity-50" data-testid="re-save"><Save size={16}/> {saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
      <style>{`.kc-rinput { width: 100%; padding: 0.625rem 0.75rem; border: 1px solid var(--kc-border-strong); border-radius: var(--kc-radius-sm); font-size: 0.95rem; outline: none; background: white; }
      .kc-rinput:focus { border-color: var(--kc-gold); box-shadow: 0 0 0 2px var(--kc-gold-soft); }`}</style>
    </div>
  );
}

const Field = ({ label, value, onChange, multiline, testid }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-wider mb-1">{label}</label>
    {multiline ? (
      <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={3} className="kc-rinput" data-testid={testid}/>
    ) : (
      <input value={value || ''} onChange={(e) => onChange(e.target.value)} className="kc-rinput" data-testid={testid}/>
    )}
  </div>
);
